import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Minus } from "@phosphor-icons/react";

export default function DuplicadoTab({ exp, setExp }) {
  const dup = exp.duplicado || {};
  const upd = (key, value) => {
    setExp({ ...exp, duplicado: { ...dup, [key]: value } });
  };
  const testigos = dup.testigos || [];

  return (
    <div className="space-y-6" data-testid="duplicado-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
        <div className="coord-label text-amber-800">// TRÁMITE</div>
        <h3 className="text-lg font-bold tracking-tight text-amber-900">
          DUPLICADO DE CERTIFICADO DE TÍTULO POR PÉRDIDA
        </h3>
        <p className="text-sm text-amber-800 mt-1">
          Los <b>datos del propietario, notario y título</b> se editan en el tab "Datos".
          Los <b>testigos por defecto</b> se configuran en tu Perfil.
        </p>
      </div>

      {/* Solicitud + Compulsa en un solo bloque compacto */}
      <div className="bg-white border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-3">Datos del Trámite</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="coord-label">Registro de Títulos Destino</Label>
            <Input
              data-testid="dup-registro-destino"
              value={dup.registro_titulos_destino || ""}
              onChange={(e) => upd("registro_titulos_destino", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="REGISTRO DE TITULOS SANTO DOMINGO"
            />
          </div>
          <div>
            <Label className="coord-label">Modo de Servicio</Label>
            <Select
              value={dup.modo_servicio || "normal"}
              onValueChange={(v) => {
                upd("modo_servicio", v);
                upd("tarifa_servicio", v === "express" ? "8000.00" : "4000.00");
              }}
            >
              <SelectTrigger className="mt-1 rounded-sm" data-testid="dup-modo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal (RD$4,000)</SelectItem>
                <SelectItem value="express">Express 017 (RD$8,000)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="coord-label">No. Registro Ayuntamiento (Compulsa, opcional)</Label>
            <Input
              data-testid="dup-compulsa-numero"
              value={dup.compulsa_registro_numero || ""}
              onChange={(e) => upd("compulsa_registro_numero", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="(dejar vacío si aún no se registra)"
            />
          </div>
          <div>
            <Label className="coord-label">Particularidad (opcional, raro)</Label>
            <Textarea
              data-testid="dup-particularidad"
              value={dup.particularidad || ""}
              onChange={(e) => upd("particularidad", e.target.value)}
              className="mt-1 rounded-sm text-sm min-h-[60px]"
              placeholder="Ej: el 2do apellido contiene error de sentencia..."
            />
          </div>
        </div>
      </div>

      {/* Testigos por expediente (opcional - los del perfil son los defaults) */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold text-slate-900">Testigos del Acto (opcional)</h4>
          <Button
            data-testid="dup-add-testigo-btn"
            size="sm" variant="outline"
            onClick={() => upd("testigos", [
              ...testigos,
              { nombre: "", nacionalidad: "dominicano", estado_civil: "soltero", cedula: "", domicilio: "" }
            ])}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Sobrescribir testigos
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Si dejas vacío, se usan los <b>testigos por defecto</b> de tu Perfil. Solo añade aquí si
          este caso usa testigos distintos.
        </p>
        {testigos.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            Usando los testigos del Perfil.
          </div>
        ) : (
          <div className="space-y-3">
            {testigos.map((t, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`dup-testigo-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">TESTIGO #{i + 1}</span>
                  <Button
                    data-testid={`dup-testigo-del-${i}`}
                    size="sm" variant="ghost"
                    onClick={() => upd("testigos", testigos.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  >
                    <Minus size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`dup-testigo-nombre-${i}`}
                    value={t.nombre || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Nombre completo"
                    className="rounded-sm"
                  />
                  <Input
                    data-testid={`dup-testigo-cedula-${i}`}
                    value={t.cedula || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    data-testid={`dup-testigo-domicilio-${i}`}
                    value={t.domicilio || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Domicilio"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
